import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { config } from "../environment";
import { useDeleteLastBranch } from "../hooks/useDeleteLastBranch";
import { useGetPresence } from "../hooks/useGetPresence";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useUrlSort } from "../hooks/useUrlSort";
import { branchLastActivity } from "../lib/branch";
import { commitConnectorIds, connectorAvatarItems } from "../lib/connector";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { makeUrl, routes, useCurrentUrl } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { ActivityFacepile } from "./ActivityFacepile";
import { Backdrop } from "./Backdrop";
import { BranchesTabHeader, type BranchesTab } from "./BranchesTabHeader";
import {
  BuildStatus,
  getBranchListStatus,
  type StatusFilterValue,
  statusFilterOrder,
} from "./BuildStatus";
import { Button } from "./Button";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorAvatars } from "./ConnectorAvatars";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { Icon } from "./Icon";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "./ListFilter";
import { ListTable } from "./ListTable";
import { MainScrollContainer } from "./MainScrollContainer";
import { Modal } from "./Modal";
import { SpaceBadge } from "./SpaceBadge";
import { MetaSep } from "./MetaSeparator";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WorkflowBadge } from "./WorkflowBadge";
import {
  workflowListFilters,
  workflowListSortColumns,
} from "./workflowListFilters";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CleanupModalContent = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

const CleanupModalTitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CleanupModalBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CleanupBranchList = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CleanupBranchLink = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  & > span:not(.arrow-icon) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const CleanupBranchBadge = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  position: relative;

  & > div:first-child {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    overflow: hidden;
    opacity: 0.5;
  }
`;

const blueprintOverlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  overflow: "visible",
};

function BlueprintOverlay() {
  return (
    <svg
      style={blueprintOverlayStyle}
      viewBox="0 0 2 2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0"
        y="0"
        width="2"
        height="2"
        rx="0.5"
        ry="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="0.5"
        y1="-0.1875"
        x2="0.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="1.5"
        y1="-0.1875"
        x2="1.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="-0.1875"
        y1="0.5"
        x2="2.1875"
        y2="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="-0.1875"
        y1="1.5"
        x2="2.1875"
        y2="1.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="0.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="1.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="0.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="1.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
    </svg>
  );
}

type UserBranch = QueryRowType<ReturnType<typeof queries.userBranches>>;
type BranchesWorkflow = NonNullable<UserBranch["workflow"]>;
type BranchesSpace = NonNullable<BranchesWorkflow["space"]>;
type BranchesCreator = NonNullable<UserBranch["user"]>;
type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectedAppsIndex>
>;

function getBranchApps(branch: UserBranch): string[] {
  return commitConnectorIds(branch);
}

export function Branches() {
  const z = useZero();
  const isMobile = useIsMobile();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectedAppsIndex());
  const [branches, branchesResult] = useQuery(
    queries.userBranches({ activitySince })
  );
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const [favorites] = useQuery(queries.userFavoriteWorkflowIds());
  const connectorById = new Map(allConnectors.map((a) => [a.id, a]));
  const presenceMap = new Map(
    workflowViews.map((v) => [v.workflowId, v.lastSeenAt])
  );
  const favoriteWorkflowIds = new Set(favorites.map((f) => f.workflowId));
  const displayName = useSpaceDisplayName();
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.branches,
    workflowListSortColumns
  );
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [confirmDeleteBranchId, setConfirmDeleteBranchId] = useState<
    string | null
  >(null);
  const deleteLastBranch = useDeleteLastBranch();
  const {
    status: statusParam,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    tab: tabParam,
  } = useCurrentUrl(routes.branches);
  const activeTab: BranchesTab =
    tabParam === "autofix" || tabParam === "autotune" ? tabParam : "branches";
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const spaceFilters = parseMultiFilter(spaceParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);

  // Filter out branches without valid workflow data
  const validBranches = branches
    .filter((d) => d.workflow)
    .map((d) => ({
      branch: d,
      workflow: d.workflow!,
      space: d.workflow!.space!,
    }));

  const branchTab = (item: (typeof validBranches)[number]): BranchesTab =>
    item.workflow.autofixBranchId === item.branch.id
      ? "autofix"
      : item.workflow.autoImproveBranchId === item.branch.id
        ? "autotune"
        : "branches";
  const branchesCount = validBranches.filter(
    (i) => branchTab(i) === "branches"
  ).length;
  const autofixCount = validBranches.filter(
    (i) => branchTab(i) === "autofix"
  ).length;
  const autoImproveCount = validBranches.filter(
    (i) => branchTab(i) === "autotune"
  ).length;
  const tabBranches = validBranches.filter((i) => branchTab(i) === activeTab);

  const getBranchBuildStatus = (item: (typeof validBranches)[number]) =>
    getBranchListStatus(item.branch, presenceMap.get(item.workflow.id));

  const availableStatuses = new Set<StatusFilterValue>();
  const availableSpaces = new Map<string, BranchesSpace>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, BranchesCreator>();
  for (const item of tabBranches) {
    const status = getBranchBuildStatus(item);
    if (status) availableStatuses.add(status);
    if (item.space) availableSpaces.set(item.space.id, item.space);
    for (const appId of getBranchApps(item.branch)) {
      availableAppIds.add(appId);
    }
    if (item.branch.userId && item.branch.user) {
      availableCreators.set(item.branch.userId, item.branch.user);
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableSpaceOptions = [...availableSpaces.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const filteredBranches = tabBranches.filter((item) => {
    if (statusFilter !== null) {
      if (getBranchBuildStatus(item) !== statusFilter) return false;
    }
    if (spaceFilters.length > 0) {
      if (!item.space || !spaceFilters.includes(item.space.id)) return false;
    }
    if (appFilters.length > 0) {
      const ids = getBranchApps(item.branch);
      if (!appFilters.some((id) => ids.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (!item.branch.userId || !creatorFilters.includes(item.branch.userId))
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    space: {
      options: availableSpaceOptions,
      values: spaceFilters,
      displayName,
    },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedBranches = [...filteredBranches].sort((a, b) => {
    if (!sortColumn) {
      const aTime = branchLastActivity(a.branch);
      const bTime = branchLastActivity(b.branch);
      return bTime - aTime;
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.branch.name.toLowerCase();
        bVal = b.branch.name.toLowerCase();
        break;
      case "space":
        aVal = displayName(a.space).toLowerCase();
        bVal = displayName(b.space).toLowerCase();
        break;
      case "createdAt":
        aVal = a.workflow.createdAt;
        bVal = b.workflow.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (item: typeof a) => {
          const status = getBranchListStatus(
            item.branch,
            presenceMap.get(item.workflow.id)
          );
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 +
            (item.branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = a.branch.user?.name?.toLowerCase() ?? "";
        bVal = b.branch.user?.name?.toLowerCase() ?? "";
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedBranches = sortedBranches;
  const { presentByBranch: presentUserIdsByBranch } = useGetPresence({
    enabled: branchesResult.type === "complete",
  });

  const hasBuildStatus = displayedBranches.some(
    ({ branch, workflow }) =>
      getBranchListStatus(branch, presenceMap.get(workflow.id)) != null
  );

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const staleBranches = tabBranches.filter(({ branch }) => {
    const lastActivity = branchLastActivity(branch);
    return lastActivity < thirtyDaysAgo;
  });

  const handleCleanup = () => {
    for (const { branch } of staleBranches) {
      z.mutate(sharedMutators.deleteBranch({ branchId: branch.id }));
    }
    setCleanupModalOpen(false);
  };

  return (
    <>
      <title>{`Branches • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <Container>
          <BranchesTabHeader
            branchesCount={branchesCount}
            autofixCount={autofixCount}
            autoImproveCount={autoImproveCount}
          />
          <MainScrollContainer>
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Branch" }
                  : {
                      name: "Branch",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      space: "Space",
                      apps: "Connectors",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={
                <>
                  {staleBranches.length > 0 ? (
                    <Tooltip content="Tidy up">
                      <Button
                        variant="secondary"
                        icon="brush-cleaning"
                        onClick={() => setCleanupModalOpen(true)}
                      />
                    </Tooltip>
                  ) : null}
                  <ListFilter filters={filters} />
                </>
              }
              hideHeadersWhenEmpty={tabBranches.length === 0}
              emptyState={
                branchesResult.type === "complete" ? (
                  tabBranches.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="git-branch"
                    />
                  ) : activeTab === "autofix" ? (
                    <EmptyState
                      title="No autofix branches"
                      description="When 3B fixes a failing workflow, the fix branch appears here for review."
                      iconName="radar"
                    />
                  ) : activeTab === "autotune" ? (
                    <EmptyState
                      title="No Autotune branches"
                      description="When 3B tunes a workflow to run better, it appears here for review."
                      iconName="audio-waveform"
                    />
                  ) : (
                    <EmptyState
                      title="No branches"
                      description="Workflow changes appear here as branches until they’re pushed live."
                      iconName="git-branch"
                    />
                  )
                ) : null
              }
              items={displayedBranches.map(({ branch, workflow, space }) => {
                return {
                  key: branch.id,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: space?.id ?? "",
                    workflowId: workflow.id,
                    branchId: branch.id,
                  }),
                  name: (hover) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        minWidth: 0,
                      }}
                    >
                      <WorkflowBadge
                        workflow={workflow}
                        hover={hover}
                        blueprint
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <Text
                          ellipsis
                          variant="text-regular-md"
                          color="var(--text-primary)"
                        >
                          {branch.name}
                        </Text>
                        <Text
                          ellipsis
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {workflow.name}
                          <MetaSep />
                          Last updated{" "}
                          {formatRelativeTime(branchLastActivity(branch))}
                        </Text>
                      </div>
                    </div>
                  ),
                  activity: (
                    <ActivityFacepile
                      contributors={branch.contributors}
                      presentUserIds={presentUserIdsByBranch.get(branch.id)}
                    />
                  ),
                  space: space ? <SpaceBadge space={space} /> : null,
                  buildStatus: (
                    <BuildStatus
                      status={getBranchListStatus(
                        branch,
                        presenceMap.get(workflow.id)
                      )}
                    />
                  ),
                  apps: (
                    <ConnectorAvatars
                      connected={connectorAvatarItems(
                        getBranchApps(branch),
                        connectorById
                      )}
                    />
                  ),
                  createdAt: (
                    <Text variant="text-small">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  ),
                  createdBy: (
                    <Text
                      variant="text-small"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {branch.user?.name}
                    </Text>
                  ),
                  actions: (hover) => (
                    <BranchActions
                      hover={hover}
                      spaceId={space?.id ?? ""}
                      workflowId={workflow.id}
                      branchId={branch.id}
                      isFavorite={favoriteWorkflowIds.has(workflow.id)}
                      onToggleFavorite={() =>
                        z.mutate(
                          sharedMutators.toggleWorkflowFavorite({
                            workflowId: workflow.id,
                          })
                        )
                      }
                      onDeleteBranch={() => {
                        if (workflow.branches.length <= 1) {
                          deleteLastBranch.confirm(branch.id, space?.id ?? "");
                        } else {
                          setConfirmDeleteBranchId(branch.id);
                        }
                      }}
                    />
                  ),
                };
              })}
            />
          </MainScrollContainer>
        </Container>
      </Backdrop>
      <Modal open={cleanupModalOpen} onClose={() => setCleanupModalOpen(false)}>
        <CleanupModalContent>
          {staleBranches.length > 0 ? (
            <>
              <CleanupModalTitleBlock>
                <Text variant="text-regular-md" color="var(--text-primary)">
                  Tidy up
                </Text>
                <Text variant="text-regular" color="var(--text-secondary)">
                  You have {staleBranches.length}{" "}
                  {staleBranches.length === 1 ? "branch" : "branches"} with no
                  chats or runs in the last 30 days. Remove{" "}
                  {staleBranches.length === 1 ? "it" : "them"} to keep things
                  tidy.
                </Text>
              </CleanupModalTitleBlock>
              <CleanupModalBody>
                {(() => {
                  const deletedWorkflows = staleBranches.filter(
                    ({ workflow }) => workflow.branches.length <= 1
                  );
                  if (deletedWorkflows.length === 0) return null;
                  return (
                    <Text variant="text-regular" color="var(--text-secondary)">
                      {deletedWorkflows.length === 1
                        ? `This will also delete the "${deletedWorkflows[0]!.workflow.name}" workflow, since its only branch is stale.`
                        : `This will also delete ${deletedWorkflows.length} workflows whose only branch is stale.`}
                    </Text>
                  );
                })()}
                <CleanupBranchList>
                  {staleBranches.map(({ branch, workflow, space }) => {
                    const badgeConfig = parseBadgeConfig(
                      workflow.badgeSeed,
                      workflow.id
                    );
                    const willDeleteWorkflow = workflow.branches.length <= 1;
                    return (
                      <CleanupBranchLink
                        key={branch.id}
                        href={makeUrl(routes.space.workflow, {
                          spaceId: space?.id ?? "",
                          workflowId: workflow.id,
                          branchId: branch.id,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <CleanupBranchBadge>
                          <div>
                            <CircleMesh
                              key={workflow.badgeSeed}
                              targetCircleSize={8}
                              seed={badgeConfig.seed}
                              mutingIntensity={0}
                              animate={false}
                              restrictedColors={badgeConfig.colors}
                              grayscale={workflow.pausedAt != null}
                            />
                          </div>
                          <BlueprintOverlay />
                        </CleanupBranchBadge>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Text
                            variant="text-regular"
                            color="var(--text-primary)"
                            ellipsis
                          >
                            {branch.name}
                          </Text>
                          {willDeleteWorkflow && (
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                            >
                              Workflow will also be deleted
                            </Text>
                          )}
                        </div>
                        <span className="arrow-icon">
                          <Icon name="arrow-up-right" size={12} />
                        </span>
                      </CleanupBranchLink>
                    );
                  })}
                </CleanupBranchList>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button size="l" variant="danger" onClick={handleCleanup}>
                    {staleBranches.length === 1 ? "Delete" : "Delete all"}
                  </Button>
                </div>
              </CleanupModalBody>
            </>
          ) : (
            <CleanupModalTitleBlock>
              <Text variant="text-regular-md" color="var(--text-primary)">
                Tidy up
              </Text>
              <Text variant="text-regular" color="var(--text-secondary)">
                Nothing to tidy — all your branches have been active in the last
                30 days. You're already on top of things.
              </Text>
            </CleanupModalTitleBlock>
          )}
        </CleanupModalContent>
      </Modal>
      <ConfirmModal
        open={confirmDeleteBranchId !== null}
        onClose={() => setConfirmDeleteBranchId(null)}
        onConfirm={() => {
          if (confirmDeleteBranchId) {
            z.mutate(
              sharedMutators.deleteBranch({ branchId: confirmDeleteBranchId })
            );
          }
          setConfirmDeleteBranchId(null);
        }}
        title="Delete branch"
        description="This branch and its history will be permanently removed."
        confirmLabel="Delete branch"
        destructive
      />
      {deleteLastBranch.modal}
    </>
  );
}

function BranchActions({
  hover,
  spaceId,
  workflowId,
  branchId,
  isFavorite,
  onToggleFavorite,
  onDeleteBranch,
}: {
  hover: boolean;
  spaceId: string;
  workflowId: string;
  branchId: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onDeleteBranch: () => void;
}) {
  const canWrite = useHasAccess("space:write", spaceId);
  return (
    <div style={{ opacity: hover ? 1 : 0, marginLeft: "auto" }}>
      <Dropdown
        opener={<Button variant="secondary" icon="more" />}
        minWidth={160}
        sections={[
          {
            items: [
              {
                type: "link",
                left: <Icon name="pencil" size={16} />,
                label: "Edit",
                to: makeUrl(routes.space.workflow, {
                  spaceId,
                  workflowId,
                  branchId,
                }),
              },
              {
                type: "button",
                left: <Icon name="link-2" size={16} />,
                label: "Copy link",
                onClick: () => {
                  const url =
                    window.location.origin +
                    makeUrl(routes.space.workflow, {
                      spaceId,
                      workflowId,
                    });
                  void writeToClipboard(url);
                },
              },
              {
                type: "button",
                left: (
                  <Icon name={isFavorite ? "heart-crack" : "heart"} size={16} />
                ),
                label: isFavorite ? "Remove favorite" : "Favorite",
                onClick: onToggleFavorite,
              },
            ],
          },
          {
            items: [
              {
                type: "button",
                left: <Icon name="trash" size={16} />,
                label: "Delete branch",
                disabled: !canWrite,
                onClick: () => {
                  if (!canWrite) return;
                  onDeleteBranch();
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}
